import React, {useId} from "react";
import {Avatar} from "primereact/avatar";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {confirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";
import {ProgressSpinner} from "primereact/progressspinner";
import {useTranslate} from "../../../system/useTranslate";

export const RTL_DROPDOWN_PROPS = {
    panelClassName: "rtl text-xs",
    pt: {
        root: {className: "w-full text-xs"},
        input: {className: "text-xs text-right"},
        item: {className: "text-xs text-right"},
        filterInput: {className: "text-xs text-right"},
    },
};

export const RTL_DATATABLE_PROPS = {
    className: "rtl text-xs",
    paginatorClassName: "rtl text-xs",
    pt: {
        root: {className: "rtl text-xs"},
        table: {className: "text-xs"},
        headerRow: {className: "text-xs"},
        bodyRow: {className: "text-xs"},
    },
};

export function confirmSalesDelete(translate, accept) {
    confirmDialog({
        header: translate("sales-financial-record-delete-title"),
        message: translate("sales-financial-record-delete-message"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: translate("delete"),
        rejectLabel: translate("cancel"),
        acceptClassName: "p-button-danger text-xs gap-2",
        rejectClassName: "p-button-text text-xs",
        className: "rtl text-xs",
        accept,
    });
}

const toneClasses = {
    orange: "bg-orange-50 text-orange-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
};

export function SalesSectionHeader({title, description, actions}) {
    return (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
            <div className="flex flex-column gap-1">
                <span className="text-base font-bold text-900">{title}</span>
                {description && <span className="text-xs text-600 line-height-3">{description}</span>}
            </div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
    );
}

export function StatCard({label, value, icon, tone = "orange"}) {
    return (
        <div className="col-12 sm:col-6 xl:col-3">
            <Card
                className="h-full border-1 border-200 shadow-none"
                pt={{body: {className: "p-3 h-full"}, content: {className: "p-0 h-full"}}}
            >
                <div className="flex align-items-center gap-3 h-full">
                    <Avatar
                        icon={icon}
                        size="large"
                        shape="circle"
                        className={toneClasses[tone] || toneClasses.orange}
                    />
                    <div className="flex flex-column gap-1">
                        <span className="text-xs text-600">{label}</span>
                        <span className="text-lg font-bold text-900">{value}</span>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export function TableCard({children, className = ""}) {
    return (
        <Card
            className={`border-1 border-200 shadow-none overflow-hidden ${className}`}
            pt={{body: {className: "p-0"}, content: {className: "p-0"}}}
        >
            {children}
        </Card>
    );
}

export function DataState({loading, error, empty, children}) {
    const translate = useTranslate();

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center min-h-12rem surface-50">
                <ProgressSpinner/>
            </div>
        );
    }
    if (error) {
        return <Message className="w-full text-xs" severity="error" text={error} pt={{text: {className: "text-xs"}}}/>;
    }
    if (empty) {
        return <Message className="w-full text-xs" severity="info" text={translate("sales-empty-data")} pt={{text: {className: "text-xs"}}}/>;
    }
    return children;
}

export function FormField({label, required, children, hint, className}) {
    const translate = useTranslate();
    const resolvedHint = hint || `${translate("form-field-purpose-prefix")}${label}${translate("form-field-purpose-suffix")}`;
    return (
        <div className={`${className || "col-12 md:col-6"} flex flex-column gap-2`}>
            <label className="text-xs font-bold text-700">
                {label}{required && <span className="text-red-500 mr-1">*</span>}
            </label>
            {children}
            <small className="text-xs text-500 line-height-3">{resolvedHint}</small>
        </div>
    );
}

export function ClearableDropdown({
    value,
    onChange,
    containerClassName = "w-full",
    className = "w-full text-xs",
    ...dropdownProps
}) {
    const translate = useTranslate();
    const clearSelection = (event) => {
        onChange?.({
            originalEvent: event,
            value: null,
            target: {name: dropdownProps.name, value: null},
        });
    };

    return (
        <div className={`flex align-items-center gap-2 ${containerClassName}`}>
            <Dropdown
                {...RTL_DROPDOWN_PROPS}
                {...dropdownProps}
                className={`${className} flex-1`}
                value={value}
                onChange={onChange}
            />
            <Button
                type="button"
                className="text-xs gap-2 flex-shrink-0"
                icon="pi pi-times"
                text
                rounded
                disabled={dropdownProps.disabled || value === null || value === undefined || value === ""}
                aria-label={translate("sales-clear-selection")}
                tooltip={translate("sales-clear-selection")}
                tooltipOptions={{className: "rtl text-xs"}}
                onClick={clearSelection}
            />
        </div>
    );
}

export function SalesMultiSelect({value, options, onChange, placeholderKey, searchPlaceholderKey}) {
    const translate = useTranslate();
    const optionKey = (item) => String(item?.documentId ?? item?.id ?? item?.value ?? item ?? "");
    const normalizedOptions = (options || []).map((option) => {
        const {value: optionValue, ...optionWithoutValue} = option || {};
        const identifier = option?.documentId ?? option?.id ?? optionValue;
        const relationIdentifier = option?.documentId || option?.id != null
            ? {}
            : typeof identifier === "number"
                ? {id: identifier}
                : {documentId: identifier};

        return {
            ...optionWithoutValue,
            ...relationIdentifier,
            _salesKey: optionKey(option),
            title: option?.title || option?.label,
        };
    });
    const optionsByKey = new Map(normalizedOptions.map((option) => [optionKey(option), option]));
    const selectedOptions = (value || []).map((item) => optionsByKey.get(optionKey(item))).filter(Boolean);
    const ItemTemplate = (data) => data ? (
        <div className="w-fit flex align-items-center gap-1 text-xs">
            {data?.title}
        </div>
    ) : (
        <div className="p-2 text-xs">{translate(placeholderKey)}</div>
    );
    const SelectedItemTemplate = (data) => (
        <div className="w-fit flex align-items-center gap-1 text-xs p-1 px-2 surface-100 border-round-2xl">
            {data?.title}
        </div>
    );

    return (
        <div className="border-round border-1 border-300 flex justify-content-between pr-2">
            <MultiSelect
                selectedItemTemplate={SelectedItemTemplate}
                filterTemplate={(filterOptions) => (
                    <InputText
                        placeholder={translate(searchPlaceholderKey)}
                        onChange={filterOptions.filterOptions.filter}
                        className="p-2 w-full text-xs"
                    />
                )}
                filter
                filterBy="title"
                dataKey="_salesKey"
                optionLabel="title"
                placeholder={translate(placeholderKey)}
                pt={{
                    root: () => ({className: "w-full border-none shadow-none"}),
                    label: () => ({className: "flex gap-1"}),
                    labelContainer: () => ({className: "align-items-center flex pr-2"}),
                    trigger: () => ({className: "p-3"}),
                    filterInput: () => ({className: "p-0"}),
                    header: () => ({className: "p-2 flex-row-reverse"}),
                    item: () => ({className: "flex-row-reverse justify-content-between p-2"}),
                }}
                panelClassName="rtl"
                onChange={onChange}
                itemTemplate={ItemTemplate}
                options={normalizedOptions}
                value={selectedOptions}
            />
        </div>
    );
}

export function SwitchField({label, checked, onChange, disabled, className, hint}) {
    const translate = useTranslate();
    const resolvedHint = hint || `${translate("form-switch-purpose-prefix")}${label}${translate("form-switch-purpose-suffix")}`;
    return (
        <div className={className || "col-12 md:col-6"}>
            <Card
                className="border-1 border-200 shadow-none"
                pt={{body: {className: "p-3"}, content: {className: "p-0"}}}
            >
                <div className="flex align-items-center justify-content-between gap-2">
                    <span className="text-xs font-bold text-700">{label}</span>
                    <InputSwitch checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.value)}/>
                </div>
                <small className="block text-xs text-500 line-height-3 mt-2">{resolvedHint}</small>
            </Card>
        </div>
    );
}

export function RepeatableTextFields({values = [], onChange, addLabelKey = "sales-add-feature", placeholderKey = "sales-feature-placeholder"}) {
    const translate = useTranslate();
    const items = Array.isArray(values) ? values : [];
    const updateItem = (index, nextValue) => {
        onChange(items.map((item, itemIndex) => itemIndex === index ? nextValue : item));
    };
    const removeItem = (index) => {
        onChange(items.filter((_, itemIndex) => itemIndex !== index));
    };

    return (
        <div className="flex flex-column gap-2">
            {items.map((item, index) => (
                <div className="flex align-items-stretch gap-2" key={`feature-${index}`}>
                    <InputText
                        className="flex-1 text-xs text-right"
                        value={item}
                        placeholder={translate(placeholderKey)}
                        onChange={(event) => updateItem(index, event.target.value)}
                    />
                    <Button
                        type="button"
                        icon="pi pi-trash"
                        severity="danger"
                        outlined
                        className="text-xs gap-2"
                        aria-label={translate("sales-remove-feature")}
                        onClick={() => removeItem(index)}
                    />
                </div>
            ))}
            <Button
                type="button"
                icon="pi pi-plus"
                label={translate(addLabelKey)}
                outlined
                severity="secondary"
                className="text-xs align-self-start gap-2"
                onClick={() => onChange([...items, ""])}
            />
        </div>
    );
}

export function FormDialog({
    visible,
    title,
    description,
    saving,
    error,
    onHide,
    onSubmit,
    children,
    submitLabelKey = "save",
    submitDisabled = false,
    renderFooter,
    className = "rtl w-11 md:w-9 lg:w-7 xl:w-6 border-round-bottom-2xl",
}) {
    const translate = useTranslate();
    const generatedFormId = useId();
    const formId = `sales-form-${generatedFormId.replace(/:/g, "")}`;
    const header = (
        <div className="flex flex-column gap-1">
            <span className="text-base font-bold text-900">{title}</span>
            <span className="text-xs text-500 line-height-3">{description || translate("form-purpose-default")}</span>
        </div>
    );
    const footer = (
        <div className="flex flex-row-reverse align-items-center justify-content-start gap-2 p-3 surface-50 border-top-1 border-300 border-round-bottom-2xl">
            <Button
                type="submit"
                form={formId}
                label={translate(submitLabelKey)}
                icon="pi pi-check"
                loading={saving}
                disabled={submitDisabled}
                className="text-xs border-round-lg gap-2"
            />
            <Button
                type="button"
                label={translate("cancel")}
                severity="secondary"
                outlined
                disabled={saving}
                className="text-xs border-round-lg"
                onClick={onHide}
            />
        </div>
    );
    const resolvedFooter = renderFooter ? renderFooter({formId}) : footer;

    return (
        <Dialog
            visible={visible}
            modal
            draggable={false}
            dismissableMask={!saving}
            closable={!saving}
            closeOnEscape={!saving}
            onHide={onHide}
            header={header}
            footer={resolvedFooter}
            className={className}
            headerClassName="border-round-top-2xl border-bottom-1 border-300 py-3"
            contentClassName="p-0"
            pt={{
                root: {className: "rtl"},
                content: {className: "p-0"},
                footer: {className: "p-0 border-round-bottom-2xl"},
            }}
        >
            <form id={formId} className="grid p-3 m-0" onSubmit={onSubmit}>
                {error && (
                    <div className="col-12">
                        <Message className="w-full text-xs" severity="error" text={error} pt={{text: {className: "text-xs"}}}/>
                    </div>
                )}
                {children}
            </form>
        </Dialog>
    );
}
